// Pure helpers for the data-subject requests screen (tested in test/privacyModel.test.ts).
import type { PrivacySubjectRequest, PrivacySubjectType } from '@/apis/opsTypes'

/** What the person types; `email` is hashed in the browser and sent as `email_hash`. */
export type SubjectInputKind = PrivacySubjectType | 'email'

export const SUBJECT_INPUTS: Array<{ value: SubjectInputKind; label: string; help: string }> = [
    {
        value: 'visitor',
        label: 'Visitor id',
        help: 'The visitor id the YOM SDK keeps in the browser, or the visitor id you sent server-side.',
    },
    { value: 'user', label: 'User id', help: 'The user id your site passed to identify().' },
    {
        value: 'email',
        label: 'Email address',
        help: 'Hashed (SHA-256, trimmed and lower-cased) in your browser; the address never leaves it.',
    },
    {
        value: 'email_hash',
        label: 'Email hash (SHA-256)',
        help: 'Hex SHA-256 of the trimmed, lower-cased address.',
    },
]

/** SHA-256 of the normalised address, the value ad-platform match keys carry. */
export async function emailHash(email: string): Promise<string> {
    const data = new TextEncoder().encode(email.trim().toLowerCase())
    const digest = await crypto.subtle.digest('SHA-256', data)
    return [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('')
}

/** Resolve the form input into the API's subject type + value. */
export async function toSubject(
    kind: SubjectInputKind,
    raw: string,
): Promise<{ type: PrivacySubjectType; subject: string }> {
    const value = raw.trim()
    if (kind === 'email') {
        return { type: 'email_hash', subject: await emailHash(value) }
    }
    return { type: kind, subject: kind === 'email_hash' ? value.toLowerCase() : value }
}

const COUNT_LABELS: Record<string, string> = {
    linked_visitors: 'devices linked',
    archive_events: 'archived events',
    archive_objects_rewritten: 'archive files rewritten',
    archive_objects_deleted: 'archive files deleted',
    d1_events: 'exact events',
    d1_events_deleted: 'exact events deleted',
    outbox_events: 'queued events',
    delivery_log_entries: 'delivery log entries',
}

/** One-line summary of what a request found / removed. */
export function describeCounts(r: Pick<PrivacySubjectRequest, 'counts' | 'status'>): string {
    const parts = Object.entries(COUNT_LABELS)
        .filter(([k]) => (r.counts[k] ?? 0) > 0)
        .map(([k, label]) => `${r.counts[k].toLocaleString('en-US')} ${label}`)
    if (parts.length > 0) {
        return parts.join(' · ')
    }
    return r.status === 'completed' ? 'Nothing found' : 'In progress'
}

export function isOpen(r: Pick<PrivacySubjectRequest, 'status'>): boolean {
    return r.status === 'queued' || r.status === 'running'
}
